"use client";

import React from "react";
import { Phone, Pause, Play, Hourglass } from "lucide-react";

export interface AgentChatVoiceButtonProps {
    isActive?: boolean;
    isConnecting?: boolean;
    isThinking?: boolean;
    isPaused?: boolean;
    onToggleVoice?: () => void;
}

/**
 * Compact Voice Call button embedded into the Unified Glassmorphism Dock.
 * Harmonized with the 3D Avatar call controls:
 * - Inactive: Emerald Green (#52c41a) with Phone icon
 * - Connecting: Amber (#f59e0b) with spinning Hourglass
 * - Thinking: Amber (#f59e0b) with spinning Hourglass
 * - Active: Coral Red (#ff4d4f) with Pause icon (click pauses the call)
 * - Paused: Amber (#faad14) with Play icon (click resumes the call)
 */
export const AgentChatVoiceButton: React.FC<AgentChatVoiceButtonProps> = ({
    isActive = false,
    isConnecting = false,
    isThinking = false,
    isPaused = false,
    onToggleVoice,
}) => {
    if (!onToggleVoice) return null;

    let buttonBg = "bg-[#52c41a] hover:bg-[#49b017] text-white shadow-sm";
    let title = "Bắt đầu cuộc gọi thoại với AI";

    if (isConnecting) {
        buttonBg = "bg-amber-500 hover:bg-amber-600 text-white cursor-wait opacity-90 shadow-sm";
        title = "Đang kết nối thoại...";
    } else if (isActive && isPaused) {
        buttonBg = "bg-[#faad14] hover:bg-[#d48806] text-white shadow-sm";
        title = "Tiếp tục cuộc trò chuyện";
    } else if (isActive) {
        if (isThinking) {
            buttonBg = "bg-amber-500 hover:bg-amber-600 text-white shadow-[0_0_12px_rgba(245,158,11,0.55)] animate-pulse";
            title = "AI đang suy nghĩ...";
        } else {
            buttonBg = "bg-[#ff4d4f] hover:bg-[#dc2626] text-white shadow-[0_0_12px_rgba(239,68,68,0.55)] animate-pulse";
            title = "Tạm dừng cuộc trò chuyện";
        }
    }

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onToggleVoice();
            }}
            disabled={isConnecting}
            title={title}
            className={`flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full transition-all active:scale-95 cursor-pointer ${buttonBg}`}
        >
            {isConnecting || (isActive && !isPaused && isThinking) ? (
                <Hourglass className="h-4 w-4 animate-spin text-white" />
            ) : isActive && isPaused ? (
                <Play className="h-4 w-4 text-white" />
            ) : isActive ? (
                <Pause className="h-4 w-4 text-white" />
            ) : (
                <Phone className="h-4 w-4 text-white" />
            )}
        </button>
    );
};

export default AgentChatVoiceButton;
